import {
  Download,
  Ellipsis,
  Loader2,
  MessageCircle,
  RotateCw,
} from "lucide-react";
import { Button } from "@/core/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/core/components/ui/dropdown-menu";
import { Stack } from "@/core/components/ui/stack";
import type { ProfitabilityRun } from "./types";
import { useReportDownload } from "./use-report-download";
import { useRerunReport } from "./use-rerun-report";

/**
 * The report header's right side. Ask AI is the thing an owner came to do with
 * a finished report, so it keeps a button; downloading and rerunning are
 * occasional, so they share the menu, the same shape the payroll header uses.
 */
export const ReportActions = ({
  businessId,
  run,
  onOpenChat,
}: {
  businessId: string;
  run: ProfitabilityRun;
  onOpenChat: () => void;
}) => {
  const { download, downloading } = useReportDownload(businessId, run);
  const { rerun, rerunning } = useRerunReport(businessId, run);
  const busy = downloading || rerunning;

  return (
    <Stack direction="row" align="center" gap="sm">
      <Button
        size="sm"
        variant="secondary"
        onClick={onOpenChat}
        leftIcon={<MessageCircle className="h-4 w-4" />}
      >
        Ask AI
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="secondary"
            size="icon"
            aria-label="More actions"
            aria-busy={busy}
            leftIcon={
              busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Ellipsis className="h-4 w-4" />
              )
            }
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-56">
          <DropdownMenuItem disabled={downloading} onSelect={download}>
            {downloading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Download XLSX
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem disabled={rerunning} onSelect={rerun}>
            <RotateCw className="h-4 w-4" />
            Run these dates again
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </Stack>
  );
};
